import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Globe, Clock, ArrowLeft, Home } from 'lucide-react';
import '../styles/auth-split.css';
import { COMPANY_LOGO_SRC } from '../constants/branding';

/**
 * Split-screen shell for customer auth pages (matches logistics hero + white form panel).
 * @param {string} [heroLead] — Optional hero paragraph under the headline (e.g. signup copy).
 * @param {boolean} [panel] — Split layout with scrollable form column (login, register).
 * @param {boolean} [longForm] — Alias for `panel` (registration).
 */
export default function AuthSplitShell({ title, subtitle, children, heroLead, panel, longForm }) {
  const usePanel = panel || longForm;

  const pageClass = ['auth-split-page', usePanel ? 'auth-split-page--panel' : '']
    .filter(Boolean)
    .join(' ');

  const columnClass = ['auth-split-form-column', usePanel ? 'auth-split-form-column--scroll' : '']
    .filter(Boolean)
    .join(' ');

  const innerClass = [
    'auth-split-form-inner',
    'auth-split-panel',
    usePanel ? 'auth-split-form-inner--panel' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={pageClass}>
      <div className="auth-split-hero" aria-hidden={false}>
        <div className="auth-split-hero__bg" />
        <div className="auth-split-hero__overlay" />
        <div className="auth-split-hero__content">
          <Link to="/" className="auth-split-brand" aria-label="Jet Shipping Cargo — Home">
            <img src={COMPANY_LOGO_SRC} alt="Jet Shipping Cargo" className="auth-split-brand__logo" decoding="async" />
            <div className="auth-split-brand__text">
              <span className="auth-split-brand__name">JET SHIPPING CARGO</span>
              <span className="auth-split-brand__tag">Global Cargo Solutions</span>
            </div>
          </Link>

          <h2 className="auth-split-hero__title">
            Delivering Possibilities. Connecting <span className="auth-split-hero__accent">the World.</span>
          </h2>
          <p className="auth-split-hero__lead">
            {heroLead ?? 'Fast, reliable and secure cargo solutions by Air, Sea or Land.'}
          </p>

          <div className="auth-split-features">
            <div className="auth-split-feature">
              <Shield className="w-6 h-6" strokeWidth={2} aria-hidden />
              <div>
                <strong>Safe &amp; Secure</strong>
                <span>Your cargo is safe with us</span>
              </div>
            </div>
            <div className="auth-split-feature">
              <Globe className="w-6 h-6" strokeWidth={2} aria-hidden />
              <div>
                <strong>Global Network</strong>
                <span>We reach 100+ countries</span>
              </div>
            </div>
            <div className="auth-split-feature">
              <Clock className="w-6 h-6" strokeWidth={2} aria-hidden />
              <div>
                <strong>On-Time Delivery</strong>
                <span>We value your time and trust</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={columnClass}>
        <div className={innerClass}>
          <Link to="/" className="auth-split-home-link">
            <ArrowLeft className="w-4 h-4" aria-hidden />
            Back to home
          </Link>
          <header className="auth-split-form-header">
            <h1>{title}</h1>
            {subtitle ? <p>{subtitle}</p> : null}
          </header>
          {children}
        </div>
        <footer className="auth-split-footer">
          <Link to="/" className="auth-split-footer-home">
            <Home className="w-4 h-4" aria-hidden />
            Home
          </Link>
          <span className="auth-split-footer-copy">
            © {new Date().getFullYear()} Jet Shipping Cargo. All rights reserved.
          </span>
        </footer>
      </div>
    </div>
  );
}
